import { useSelector } from 'react-redux'
import type { UserRoleName } from 'src/generated'
import { userRoleSelector } from 'src/redux/providers/selectors'

// ----------------------------------------------------------------------
// Who may see and act on a customer's deletion record.
//
// A Beast customer is a customer of Beast: the record is kept on file by Beast,
// erased by Beast's own automation on a rule, and the ban and the debt that
// hold one back are Beast's. The deletion queue used to open for any provider
// carrying `userRemoveRequestEnabled`, and it was never scoped to a provider,
// so one operator's console listed every customer on the platform who had asked
// to be deleted — by name and e-mail address. `userRemove` took a bare user id
// and scoped nothing at all.
//
// `superAdmin` is this console's name for Beast staff and for nobody else:
// `userRoleSelector` returns it for `auth.isAdmin` and refuses a provider row
// that calls itself that, and a provider's own users carry `admin`,
// `fleetManager` or `fleetAssistant`.
//
// One predicate, for the same reason peer-to-peer has one: the rule is applied
// in the nav, the route behind it, and the two menus that offer the Delete
// button on a customer. Four copies is three chances to disagree, and the one
// that disagrees is the one that leaks.
// ----------------------------------------------------------------------

/**
 * The roles the deletion queue admits. Spread into `roles` on a menu entry and
 * `requiredRole` on a route, so both read the same list.
 */
export const ERASURE_ROLES: readonly UserRoleName[] = ['superAdmin']

/** Whether this viewer may see or act on a deletion record. */
export const erasureVisibleTo = (
  role: UserRoleName | null | undefined,
): boolean => role != null && ERASURE_ROLES.includes(role)

/**
 * The same question for a component, which has a store but no role in hand.
 *
 * The Delete button on a customer asks this rather than repeating the role, so
 * it disappears with the queue instead of surviving it as a press the server
 * refuses.
 */
export const useErasureAllowed = (): boolean =>
  erasureVisibleTo(useSelector(userRoleSelector))
